import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Lock, Eye, Database, UserCheck, Mail, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export default function PrivacyPage() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const content = {
    fr: {
      title: 'Confidentialité',
      subtitle: 'Comment nous protégeons vos données',
      sections: [
        {
          icon: Lock,
          title: 'Chiffrement des données',
          description: 'Toutes vos données sensibles (transactions, salaire, informations personnelles) sont chiffrées avec un algorithme AES-256 avant d\'être stockées dans notre base de données.'
        },
        {
          icon: Eye,
          title: 'Données collectées',
          description: 'Nous collectons uniquement les données nécessaires au fonctionnement de l\'application : email, nom, transactions, objectifs financiers. Nous ne vendons jamais vos données à des tiers.'
        },
        {
          icon: Database,
          title: 'Stockage sécurisé',
          description: 'Vos données sont stockées sur des serveurs sécurisés en Europe, conformément au RGPD. Des sauvegardes régulières garantissent la protection contre la perte de données.'
        },
        {
          icon: UserCheck,
          title: 'Vos droits',
          description: 'Vous avez le droit d\'accéder, de modifier et de supprimer vos données à tout moment. Utilisez les paramètres de votre compte ou contactez-nous pour exercer ces droits.'
        },
        {
          icon: Mail,
          title: 'Communications',
          description: 'Nous ne vous envoyons que des emails essentiels liés à votre compte. Vous pouvez vous désabonner des notifications non essentielles à tout moment.'
        },
        {
          icon: Trash2,
          title: 'Suppression du compte',
          description: 'Vous pouvez supprimer votre compte à tout moment depuis les paramètres. Toutes vos données seront définitivement effacées sous 30 jours.'
        }
      ],
      lastUpdate: 'Dernière mise à jour : Février 2026',
      contact: 'Pour toute question sur la confidentialité, contactez-nous à privacy@spenspot.app'
    },
    en: {
      title: 'Privacy',
      subtitle: 'How we protect your data',
      sections: [
        {
          icon: Lock,
          title: 'Data encryption',
          description: 'All your sensitive data (transactions, salary, personal information) is encrypted using AES-256 algorithm before being stored in our database.'
        },
        {
          icon: Eye,
          title: 'Data collected',
          description: 'We only collect data necessary for the app to function: email, name, transactions, financial goals. We never sell your data to third parties.'
        },
        {
          icon: Database,
          title: 'Secure storage',
          description: 'Your data is stored on secure servers in Europe, in compliance with GDPR. Regular backups ensure protection against data loss.'
        },
        {
          icon: UserCheck,
          title: 'Your rights',
          description: 'You have the right to access, modify and delete your data at any time. Use your account settings or contact us to exercise these rights.'
        },
        {
          icon: Mail,
          title: 'Communications',
          description: 'We only send you essential emails related to your account. You can unsubscribe from non-essential notifications at any time.'
        },
        {
          icon: Trash2,
          title: 'Account deletion',
          description: 'You can delete your account at any time from settings. All your data will be permanently deleted within 30 days.'
        }
      ],
      lastUpdate: 'Last updated: February 2026',
      contact: 'For any privacy questions, contact us at privacy@spenspot.app'
    },
    pt: {
      title: 'Privacidade',
      subtitle: 'Como protegemos os seus dados',
      sections: [
        {
          icon: Lock,
          title: 'Encriptação de dados',
          description: 'Todos os seus dados sensíveis (transações, salário, informações pessoais) são encriptados com o algoritmo AES-256 antes de serem armazenados na nossa base de dados.'
        },
        {
          icon: Eye,
          title: 'Dados recolhidos',
          description: 'Apenas recolhemos os dados necessários para o funcionamento da aplicação: email, nome, transações, objetivos financeiros. Nunca vendemos os seus dados a terceiros.'
        },
        {
          icon: Database,
          title: 'Armazenamento seguro',
          description: 'Os seus dados são armazenados em servidores seguros na Europa, em conformidade com o RGPD. Backups regulares garantem proteção contra perda de dados.'
        },
        {
          icon: UserCheck,
          title: 'Os seus direitos',
          description: 'Tem o direito de aceder, modificar e eliminar os seus dados a qualquer momento. Utilize as definições da sua conta ou contacte-nos para exercer estes direitos.'
        },
        {
          icon: Mail,
          title: 'Comunicações',
          description: 'Apenas enviamos emails essenciais relacionados com a sua conta. Pode cancelar a subscrição de notificações não essenciais a qualquer momento.'
        },
        {
          icon: Trash2,
          title: 'Eliminação da conta',
          description: 'Pode eliminar a sua conta a qualquer momento nas definições. Todos os seus dados serão permanentemente eliminados em 30 dias.'
        }
      ],
      lastUpdate: 'Última atualização: Fevereiro 2026',
      contact: 'Para questões de privacidade, contacte-nos em privacy@spenspot.app'
    }
  };

  const c = content[language] || content.fr;

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b">
        <div className="flex items-center gap-3 p-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            <h1 className="text-xl font-bold">{c.title}</h1>
          </div>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6 max-w-2xl mx-auto">
        <motion.p 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-muted-foreground text-center"
        >
          {c.subtitle}
        </motion.p>

        <div className="space-y-4">
          {c.sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <section.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{section.title}</h3>
                      <p className="text-sm text-muted-foreground">{section.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <div className="text-center text-sm text-muted-foreground space-y-2 pt-4">
          <p>{c.lastUpdate}</p>
          <p>{c.contact}</p>
        </div>
      </div>
    </div>
  );
}
